import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  styled,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { changePassword } from "@services/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const InputContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4.5px",
  width: "100%",
});

const InputLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    height: "42px",
    borderRadius: "4px",
    "& fieldset": {
      borderColor: "#e6fafc",
    },
    "&:hover fieldset": {
      borderColor: "#bfe5ec",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0a829d",
    },
    boxShadow:
      "inset 0px 0px 8px 0px rgba(0,108,116,0.12), inset 0px 0px 4px 0px rgba(34,110,127,0.25)",
  },
  "& .MuiOutlinedInput-input": {
    padding: "11px 14px",
    fontSize: "14px",
    fontFamily: "Rethink Sans, sans-serif",
    // Hide browser's default password reveal buttons
    "&::-ms-reveal": {
      display: "none",
    },
    "&::-ms-clear": {
      display: "none",
    },
    "&[type='password']::-webkit-credentials-auto-fill-button": {
      display: "none !important",
      visibility: "hidden",
      pointerEvents: "none",
      position: "absolute",
      right: 0,
    },
  },
});

const PasswordStrengthContainer = styled(Box)({
  display: "flex",
  gap: "2px",
  alignItems: "center",
  padding: "2px",
  width: "100%",
});

const PasswordStrengthBar = styled(Box)<{ isActive: boolean }>(
  ({ isActive }) => ({
    flex: 1,
    height: "4px",
    backgroundColor: isActive ? "#0a829d" : "#e5e7eb",
    borderRadius: "16px",
  })
);

const FormContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  alignItems: "left",
  paddingTop: "8px",
  flex: 1,
  width: "100%",
  maxWidth: "500px",
});

const PasswordSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
});

const SingleInputSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
});

const ContinueButton = styled(Button)<{ isActive: boolean }>(
  ({ isActive, theme }) => ({
    background: isActive
      ? "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)"
      : "#ddf0f4",
    border: "1px solid #ffffff",
    borderRadius: "48px",
    height: "48px",
    width: "25%",
    padding: "12px 24px",
    textTransform: "none",
    fontFamily: "Rethink Sans, sans-serif",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "20px",
    color: isActive ? "#ffffff" : "#bad6dc",
    boxShadow: isActive
      ? "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)"
      : "none",
    "&:hover": {
      background: isActive
        ? "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)"
        : "#ddf0f4",
    },
    "&:disabled": {
      background: "#ddf0f4",
      color: "#bad6dc",
    },
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  })
);

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  [theme.breakpoints.down("sm")]: {
    gap: "16px",
  },
}));

const FormSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "14px",
});

const ChangePasswordHeader = styled(Box)(({ theme }) => ({
  padding: "8px 4px",
  borderBottom: "1px solid #35d7e4",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "824px",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
  },
}));

const SectionTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "18px",
  fontWeight: 600,
  color: "#065b70",
  lineHeight: "normal",
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "14px",
  fontWeight: 500,
  color: "#065b70",
  lineHeight: "normal",
});

const ChangePasswordContent: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const score = [
      minLength,
      hasUppercase,
      hasLowercase,
      hasNumbers,
      hasSpecialChar,
    ].filter(Boolean).length;
    return {
      score,
      minLength,
      hasUppercase,
      hasLowercase,
      hasNumbers,
      hasSpecialChar,
    };
  };

  const passwordValidation = validatePassword(newPassword);
  const isPasswordValid =
    passwordValidation.minLength && passwordValidation.score >= 3;
  const doPasswordsMatch =
    newPassword === confirmPassword && confirmPassword !== "";
  const isFormValid = isPasswordValid && doPasswordsMatch;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!localStorage.getItem("access_token")) {
      navigate("/forgot-password");
      return;
    }
    if (!isFormValid || !currentPassword) return;

    setLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      toast.success("Password has been changed successfully!");
      setTimeout(() => {
        navigate("/reset-password-success", {
          state: { currentView: "changepassword" },
        });
      }, 1500);
    } catch (err: any) {
      let errorMessage = "Password reset failed. Please try again.";
      try {
        const parsed = JSON.parse(err.message);
        errorMessage = parsed?.detail || parsed?.message || errorMessage;
      } catch {
        errorMessage = err.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentWrapper>
      <FormSection>
        <ChangePasswordHeader>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <SectionTitle>Set New Password</SectionTitle>
            <SectionSubtitle>
              Password must meet the following requirements:
            </SectionSubtitle>
          </Box>
        </ChangePasswordHeader>

        <Box component="ul" sx={{ mb: 0, mt: 0 }}>
          <Typography
            component="li"
            sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "14px", color: "#555" }}
          >
            Minimum 8 characters
          </Typography>
          <Typography
            component="li"
            sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "14px", color: "#555" }}
          >
            At least one uppercase letter (A–Z)
          </Typography>
          <Typography
            component="li"
            sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "14px", color: "#555" }}
          >
            At least one lowercase letter (a–z)
          </Typography>
          <Typography
            component="li"
            sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "14px", color: "#555" }}
          >
            At least one number (0–9)
          </Typography>
          <Typography
            component="li"
            sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "14px", color: "#555" }}
          >
            At least one special character (e.g., !@#$%^&*)
          </Typography>
        </Box>

        <FormContent>
          <Box
            component="form"
            onSubmit={onSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "35px",
              width: "100%",
            }}
          >
            <PasswordSection>
              <SingleInputSection>
                <InputContainer>
                  <InputLabel>Current Password</InputLabel>
                  <StyledTextField
                    fullWidth
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </InputContainer>
                <InputContainer>
                  <InputLabel>Enter New Password</InputLabel>
                  <StyledTextField
                    fullWidth
                    type={showNewPassword ? "text" : "password"}
                    variant="outlined"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="*********"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() =>
                              setShowNewPassword(!showNewPassword)
                            }
                            edge="end"
                            disableRipple
                            sx={{
                              color: "#666",
                              padding: "8px",
                              backgroundColor: "transparent !important",
                              "&:hover": {
                                backgroundColor: "transparent !important",
                              },
                            }}
                          >
                            {showNewPassword ? (
                              <VisibilityOff sx={{ fontSize: 20 }} />
                            ) : (
                              <Visibility sx={{ fontSize: 20 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </InputContainer>

                <PasswordStrengthContainer>
                  <PasswordStrengthBar
                    isActive={passwordValidation.score >= 1}
                  />
                  <PasswordStrengthBar
                    isActive={passwordValidation.score >= 2}
                  />
                  <PasswordStrengthBar
                    isActive={passwordValidation.score >= 3}
                  />
                  <PasswordStrengthBar
                    isActive={passwordValidation.score >= 4}
                  />
                  <PasswordStrengthBar
                    isActive={passwordValidation.score >= 5}
                  />
                </PasswordStrengthContainer>
              </SingleInputSection>

              <InputContainer>
                <InputLabel>Confirm New Password</InputLabel>
                <StyledTextField
                  fullWidth
                  type={showConfirmPassword ? "text" : "password"}
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="*********"
                  required
                  error={confirmPassword !== "" && !doPasswordsMatch}
                  helperText={
                    confirmPassword !== "" && !doPasswordsMatch
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                          disableRipple
                          sx={{
                            color: "#666",
                            padding: "8px",
                            backgroundColor: "transparent !important",
                            "&:hover": {
                              backgroundColor: "transparent !important",
                            },
                          }}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff sx={{ fontSize: 20 }} />
                          ) : (
                            <Visibility sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </InputContainer>
            </PasswordSection>
          </Box>
        </FormContent>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start" }}>
          <ContinueButton
            type="submit"
            disabled={loading || !isFormValid}
            isActive={isFormValid}
            onClick={onSubmit}
          >
            {loading ? (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <CircularProgress size={20} color="inherit" />
                RESETTING...
              </Box>
            ) : (
              "CONTINUE"
            )}
          </ContinueButton>
        </Box>
      </FormSection>
    </ContentWrapper>
  );
};

export default ChangePasswordContent;
